import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useClub } from '../../hooks/useClub';

// ─── Sales Dashboard (Sprint 21.3, 10/1/26) ──────────────────────────────────
// The long-deferred "Inside Sales Dashboard" backlog item. One page
// aggregating Josh's Sales-owned slice of 4 engines (Lead Engine, T2M,
// PB101, Retention-Escalated) — a triage/overview layer, not a new place to
// do the actual work. Reads GET /lead/sales-dashboard
// (getSalesDashboardOverview, queries.js). Goal-setting uses its own new
// rep_contact_goals table (day/week/month, real targets — not a prorated
// slice of a monthly number, see getLeadContactsKPI's own goal for why
// that's different) via POST /lead/sales-dashboard/goal, same admin/
// self-unlock access model as Lead Engine's existing month-only goals.
//
// Club filter (BK, 10/1/26: "ALL clubs or any combination") defaults to
// every club Josh actually works — explicitly ALL 6 (TX + WI), not a
// TX-only subset; confirmed directly, not assumed. "Totals and results" +
// "how many people were contacted per club" mean each card can expand into
// a real per-club table, not just one blended number.

const ENGINES = [
  { module: 'lead_engine',        label: 'Lead Engine',          icon: '📋', route: '/lead-pipeline' },
  { module: 't2m',                label: 'T2M (Sales-owned)',    icon: '🌱', route: '/t2m-pipeline' },
  { module: 'pb101',              label: 'PB101 (Sales-owned)',  icon: '🏓', route: '/pb101-pipeline' },
  { module: 'retention_escalated', label: 'Retention — Escalated', icon: '🔄', route: '/retention-pipeline' },
];

// Stage labels for the per-club expansion (BK, 10/2/26: "expand that
// further by stage, especially for Lead Engine"). Keys match the stage
// keys each engine's own board function already returns.
const STAGE_LABELS = {
  lead_engine: { new: 'New', working: 'Working', followUp: 'Follow Up Scheduled', trialReady: 'Trial Ready', promo: 'Promo', winBack: 'Win Back', neverMember: 'Played, Never Joined', nurture: 'Nurture' },
  t2m: { awaiting_contact: 'Awaiting Contact', contacted: 'Awaiting Response', engaged: 'Engaged', follow_up: 'Follow Up Scheduled' },
  pb101: { readyForSales: 'Ready for Sales', followUpScheduled: 'Follow Up Scheduled', notBooked: 'Not booked after PB101', notReadyYet: 'Not Ready Yet' },
  retention_escalated: { failed_payment: 'Failed Payment', voluntary_cancellation: 'Voluntary Cancellation' },
};

// Lead Engine contacts are attributed to the lead's CURRENT stage (the
// logged stage-at-contact column is empty in practice), so a contact can
// land on a lead that has since converted, been lost, or left the board.
const CONTACT_ONLY_STAGE_LABELS = { won: 'Won (since)', lost: 'Lost (since)', other: 'No longer on board' };

const GRANULARITIES = [
  { key: 'day', label: 'Daily' },
  { key: 'week', label: 'Weekly' },
  { key: 'month', label: 'Monthly' },
];

function statBoxStyle() {
  return { background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7, padding: '6px 10px' };
}

function paceColor(pacePct) {
  if (pacePct === null || pacePct === undefined) return '#888';
  if (pacePct >= 100) return '#22c55e';
  if (pacePct >= 70) return '#f59e0b';
  return '#ef4444';
}

function methodLine(contacts) {
  if (!contacts || contacts.total === 0) return 'No contacts logged yet';
  const parts = [];
  if (contacts.call) parts.push(`${contacts.call} call`);
  if (contacts.text) parts.push(`${contacts.text} text`);
  if (contacts.email) parts.push(`${contacts.email} email`);
  if (contacts.other) parts.push(`${contacts.other} other`);
  if (contacts.unrecorded) parts.push(`${contacts.unrecorded} unrecorded`);
  return parts.join(' · ') || `${contacts.total} logged`;
}

// Multi-select club filter — "All Clubs" is the explicit default and its
// own checkbox (checking it selects/clears every club at once), plus one
// checkbox per real club (reusing useClub's availableClubs for real names,
// already scoped to whatever this logged-in user is allowed to see).
function ClubFilter({ availableClubs, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const allSelected = selected.length === availableClubs.length;
  const label = allSelected ? 'All Clubs' : selected.length === 0 ? 'No clubs' : `${selected.length} club${selected.length > 1 ? 's' : ''}`;

  function toggleAll() {
    onChange(allSelected ? [] : availableClubs.map(c => c.id));
  }
  function toggleOne(id) {
    onChange(selected.includes(id) ? selected.filter(c => c !== id) : [...selected, id]);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-ghost btn-sm"
        style={{ fontSize: 12, padding: '5px 10px', border: '1px solid #333' }}
        onClick={() => setOpen(o => !o)}
      >
        🏢 {label} ▾
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, zIndex: 20, minWidth: 180,
          background: '#141414', border: '1px solid #2a2a2a', borderRadius: 7, padding: 8,
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 2px', fontWeight: 700, borderBottom: '1px solid #2a2a2a', marginBottom: 4 }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            All Clubs
          </label>
          {availableClubs.map(c => (
            <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '4px 2px', color: '#ccc' }}>
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleOne(c.id)} />
              {c.name}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// Per-club table — only worth showing once more than one club is in play;
// a single-club selection already says everything the total line does.
// Launch strip — the dashboard is the front door into the modules (BK,
// 10/2/26: "could actually become the launching point into the modules").
const LAUNCH_LINKS = [
  { label: 'Lead Engine', icon: '📋', route: '/lead-pipeline' },
  { label: 'Inbound Leads', icon: '📥', route: '/inbound' },
  { label: 'T2M', icon: '🌱', route: '/t2m-pipeline' },
  { label: 'PB101', icon: '🏓', route: '/pb101-pipeline' },
  { label: 'Retention', icon: '🔄', route: '/retention-pipeline' },
  { label: 'Member Search', icon: '🔍', route: '/crm/search' },
  { label: 'Commission', icon: '💵', route: '/commission' },
];

function LaunchStrip({ onNavigate }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
      {LAUNCH_LINKS.map(l => (
        <button
          key={l.route}
          className="btn btn-ghost btn-sm"
          style={{ fontSize: 11.5, border: '1px solid #2a2a2a' }}
          onClick={() => onNavigate(l.route)}
        >
          {l.icon} {l.label}
        </button>
      ))}
    </div>
  );
}

const FOLLOWUP_ENGINES = [
  { key: 'lead_engine', label: 'Lead Engine', route: '/lead-pipeline' },
  { key: 't2m', label: 'T2M', route: '/t2m-pipeline' },
  { key: 'pb101', label: 'PB101', route: '/pb101-pipeline' },
];

function FuCell({ n, color }) {
  return <td style={{ padding: '3px 0', textAlign: 'right', color: n ? color : '#555' }}>{n}</td>;
}

// Follow-ups across the Sales-owned engines. Each engine row opens its own
// board (the legacy /followups page reads an empty table, so it isn't
// linked here); club rows expand under each engine.
function FollowUpsCard({ followUps, clubs, clubNameById, onNavigate }) {
  const [expanded, setExpanded] = useState({});
  if (!followUps) return null;
  const all = followUps.all;
  const th = { fontWeight: 400, paddingBottom: 3, textAlign: 'right' };
  return (
    <div className="card" style={{ minWidth: 260 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>🔔 Follow-Ups</div>
        <div style={{ fontSize: 11, color: '#888' }}>{all.overdue + all.today + all.upcoming} open</div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        {[['Overdue', all.overdue, '#ef4444'], ['Today', all.today, '#f59e0b'], ['Upcoming', all.upcoming, '#3b82f6']].map(([label, n, color]) => (
          <div key={label} style={{ ...statBoxStyle(), flex: 1 }}>
            <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: n ? color : '#ccc' }}>{n}</div>
          </div>
        ))}
      </div>
      {all.stale > 0 && (
        <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 6 }}>
          {all.stale} of the overdue are more than 7 days past due
        </div>
      )}
      <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ color: '#666', textAlign: 'left' }}>
            <th style={{ fontWeight: 400, paddingBottom: 3 }}>Engine</th>
            <th style={th}>Overdue</th><th style={th}>Today</th><th style={th}>Upcoming</th>
          </tr>
        </thead>
        <tbody>
          {FOLLOWUP_ENGINES.map(e => {
            const t = followUps.byEngine?.[e.key]?.total;
            if (!t) return null;
            const isOpen = !!expanded[e.key];
            return (
              <React.Fragment key={e.key}>
                <tr style={{ borderTop: '1px solid #222', color: '#ccc' }}>
                  <td style={{ padding: '3px 0' }}>
                    {clubs.length > 1 && (
                      <span style={{ color: '#666', marginRight: 4, cursor: 'pointer' }}
                        onClick={() => setExpanded(x => ({ ...x, [e.key]: !x[e.key] }))}>{isOpen ? '▾' : '▸'}</span>
                    )}
                    <span style={{ cursor: 'pointer', color: '#93c5fd' }} onClick={() => onNavigate(e.route)}>{e.label}</span>
                  </td>
                  <FuCell n={t.overdue} color="#ef4444" /><FuCell n={t.today} color="#f59e0b" /><FuCell n={t.upcoming} color="#3b82f6" />
                </tr>
                {isOpen && clubs.map(c => {
                  const b = followUps.byEngine[e.key].byClub?.[c];
                  if (!b) return null;
                  return (
                    <tr key={`${e.key}-${c}`} style={{ color: '#888' }}>
                      <td style={{ padding: '2px 0 2px 16px' }}>{clubNameById[c] || c}</td>
                      <FuCell n={b.overdue} color="#ef4444" /><FuCell n={b.today} color="#f59e0b" /><FuCell n={b.upcoming} color="#3b82f6" />
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 9, color: '#555', marginTop: 6 }}>
        Follow-ups this rep scheduled that no later contact has followed up on yet.
      </div>
    </div>
  );
}

// Inbound lead queue — a shared queue, not owned by one rep, so these
// numbers don't change with the rep picker. Leads with no club assigned
// are shown on their own row (only when no club filter narrows the view).
function InboundCard({ inbound, clubNameById, onNavigate }) {
  const [open, setOpen] = useState(false);
  if (!inbound) return null;
  const t = inbound.total;
  const keys = Object.keys(inbound.byClub);
  return (
    <div className="card" style={{ cursor: 'pointer', minWidth: 260 }} onClick={() => onNavigate('/inbound')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>📥 Inbound Leads</div>
        <div style={{ fontSize: 11, color: '#888' }}>{t.new} waiting</div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 6 }} onClick={e => e.stopPropagation()}>
        {[['Waiting', t.new, t.new ? '#f59e0b' : '#ccc'], ['New today', t.today, '#ccc'], ['Last 7 days', t.last7, '#ccc'], ['Promoted', t.promoted, '#22c55e']].map(([label, n, color]) => (
          <div key={label} style={{ ...statBoxStyle(), flex: 1 }}>
            <div style={{ fontSize: 9, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color }}>{n}</div>
          </div>
        ))}
      </div>
      {t.oldestNewDays !== null && (
        <div style={{ fontSize: 10, color: t.oldestNewDays > 7 ? '#ef4444' : '#888' }}>
          Oldest uncontacted lead: {t.oldestNewDays} days
        </div>
      )}
      {keys.length > 1 && (
        <button className="btn btn-ghost btn-sm" style={{ fontSize: 9.5, marginTop: 6, padding: '2px 6px' }}
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
          {open ? '▾ Hide by-club breakdown' : '▸ Show by-club breakdown'}
        </button>
      )}
      {open && (
        <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse', marginTop: 6 }} onClick={e => e.stopPropagation()}>
          <thead>
            <tr style={{ color: '#666', textAlign: 'left' }}>
              <th style={{ fontWeight: 400, paddingBottom: 3 }}>Club</th>
              <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>Waiting</th>
              <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>7d</th>
              <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>Oldest</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => {
              const b = inbound.byClub[k];
              return (
                <tr key={k} style={{ borderTop: '1px solid #222', color: '#ccc' }}>
                  <td style={{ padding: '3px 0' }}>{k === 'UNROUTED' ? 'No club assigned' : (clubNameById[k] || k)}</td>
                  <td style={{ padding: '3px 0', textAlign: 'right', color: b.new ? '#f59e0b' : '#555' }}>{b.new}</td>
                  <td style={{ padding: '3px 0', textAlign: 'right' }}>{b.last7}</td>
                  <td style={{ padding: '3px 0', textAlign: 'right', color: b.oldestNewDays > 7 ? '#ef4444' : '#888' }}>
                    {b.oldestNewDays !== null ? `${b.oldestNewDays}d` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ByClubTable({ module, period, activeCountByClub, activeStagesByClub, clubNameById, clubs }) {
  const [expanded, setExpanded] = useState({});
  if (clubs.length <= 1) return null;
  const labels = STAGE_LABELS[module] || {};
  const stageKeys = Object.keys(labels);
  const hasStageContacts = module === 'lead_engine';
  return (
    <table style={{ width: '100%', fontSize: 10.5, borderCollapse: 'collapse', marginTop: 6 }}>
      <thead>
        <tr style={{ color: '#666', textAlign: 'left' }}>
          <th style={{ fontWeight: 400, paddingBottom: 3 }}>Club</th>
          <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>Active</th>
          <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>Contacts</th>
          <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>Contacted</th>
          <th style={{ fontWeight: 400, paddingBottom: 3, textAlign: 'right' }}>Reached</th>
        </tr>
        {hasStageContacts && (
          <tr><td colSpan={5} style={{ fontSize: 9, color: '#555', paddingBottom: 3 }}>
            Expanded stage rows show each lead's stage as of now, not when contacted.
          </td></tr>
        )}
      </thead>
      <tbody>
        {clubs.map(c => {
          const cc = period?.contactsByClub?.[c];
          const stages = activeStagesByClub?.[c];
          const isOpen = !!expanded[c];
          return (
            <React.Fragment key={c}>
              <tr
                style={{ borderTop: '1px solid #222', color: '#ccc', cursor: stageKeys.length ? 'pointer' : 'default' }}
                onClick={e => { e.stopPropagation(); if (stageKeys.length) setExpanded(x => ({ ...x, [c]: !x[c] })); }}
              >
                <td style={{ padding: '3px 0' }}>
                  <span style={{ color: '#666', marginRight: 4 }}>{stageKeys.length ? (isOpen ? '▾' : '▸') : ''}</span>
                  {clubNameById[c] || c}
                </td>
                <td style={{ padding: '3px 0', textAlign: 'right' }}>{activeCountByClub?.[c] ?? 0}</td>
                <td style={{ padding: '3px 0', textAlign: 'right' }}>{cc?.total ?? 0}</td>
                <td style={{ padding: '3px 0', textAlign: 'right', color: '#facc15' }}>{cc?.peopleContacted ?? 0}</td>
                <td style={{ padding: '3px 0', textAlign: 'right', color: '#22c55e' }}>{cc?.peopleReached ?? 0}</td>
              </tr>
              {isOpen && stages && (() => {
                const stageContacts = cc?.byStage || {};
                const extraKeys = hasStageContacts
                  ? Object.keys(stageContacts).filter(k => !labels[k])
                  : [];
                return [...stageKeys, ...extraKeys].map(k => {
                  const sc = stageContacts[k];
                  const isExtra = !labels[k];
                  return (
                    <tr key={`${c}-${k}`} style={{ color: '#888' }}>
                      <td style={{ padding: '2px 0 2px 16px' }}>{labels[k] || CONTACT_ONLY_STAGE_LABELS[k] || k}</td>
                      <td style={{ padding: '2px 0', textAlign: 'right' }}>{isExtra ? '' : (stages[k] ?? 0)}</td>
                      <td style={{ padding: '2px 0', textAlign: 'right' }}>{hasStageContacts ? (sc?.contacts ?? 0) : ''}</td>
                      <td style={{ padding: '2px 0', textAlign: 'right' }}>{hasStageContacts ? (sc?.people ?? 0) : ''}</td>
                    </tr>
                  );
                });
              })()}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function EngineCard({ engine, card, onNavigate, clubNameById, clubs }) {
  const [granularity, setGranularity] = useState('week');
  const [byClubOpen, setByClubOpen] = useState(false);

  const period = card?.periods?.[granularity];
  const pacePct = period?.pacePct ?? null;
  const peopleContacted = period?.contacts?.peopleContacted ?? 0;
  const peopleReached = period?.contacts?.peopleReached ?? 0;
  const reachedPct = period?.reachedPacePct ?? null;

  return (
    <div className="card" style={{ cursor: 'pointer', minWidth: 260 }} onClick={() => onNavigate(engine.route)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>{engine.icon} {engine.label}</div>
        <div style={{ fontSize: 11, color: '#888' }}>{card?.activeCount ?? 0} active</div>
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 8 }} onClick={e => e.stopPropagation()}>
        {GRANULARITIES.map(g => (
          <button
            key={g.key}
            onClick={() => setGranularity(g.key)}
            style={{
              fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${granularity === g.key ? '#3b82f6' : '#333'}`,
              background: granularity === g.key ? '#3b82f615' : '#111',
              color: granularity === g.key ? '#3b82f6' : '#777',
            }}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div style={statBoxStyle()} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          People contacted — {GRANULARITIES.find(g => g.key === granularity).label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: period?.goalAmount != null ? paceColor(pacePct) : '#ccc' }}>
          {peopleContacted}{period?.goalAmount != null ? ` / ${period.goalAmount}` : ''}
        </div>
        <div style={{ fontSize: 9, color: '#666' }}>
          {pacePct !== null ? `${pacePct}% of goal` : 'No goal locked for this month'}
          {' · '}{period?.contacts?.total ?? 0} contacts logged
        </div>
        <div style={{ fontSize: 9.5, color: '#888', marginTop: 4 }}>
          {methodLine(period?.contacts)}
        </div>
      </div>

      <div style={{ ...statBoxStyle(), marginTop: 6 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 9.5, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          People reached — {GRANULARITIES.find(g => g.key === granularity).label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: period?.reachedGoalAmount != null ? paceColor(reachedPct) : '#ccc' }}>
          {peopleReached}{period?.reachedGoalAmount != null ? ` / ${period.reachedGoalAmount}` : ''}
        </div>
        <div style={{ fontSize: 9, color: '#666' }}>
          {reachedPct !== null ? `${reachedPct}% of goal` : 'No reached goal locked'}
          {peopleContacted > 0 ? ` · ${Math.round((peopleReached / peopleContacted) * 100)}% of those contacted` : ''}
        </div>

        {clubs.length > 1 && (
          <button
            className="btn btn-ghost btn-sm" style={{ fontSize: 9.5, marginTop: 6, padding: '2px 6px' }}
            onClick={e => { e.stopPropagation(); setByClubOpen(o => !o); }}
          >
            {byClubOpen ? '▾ Hide by-club breakdown' : '▸ Show by-club breakdown'}
          </button>
        )}
        {byClubOpen && (
          <ByClubTable module={engine.module} period={period} activeCountByClub={card?.activeCountByClub} activeStagesByClub={card?.activeStagesByClub} clubNameById={clubNameById} clubs={clubs} />
        )}
      </div>
    </div>
  );
}

// ─── Monthly goal plan (BK, 10/2/26) ─────────────────────────────────────────
// Agreed with the rep in a meeting, entered by an admin, then LOCKED. Only an
// admin can edit, lock or unlock (enforced server-side); the rep sees their
// own plan and progress read-only. One plan per month: a month goal plus a
// standing weekly and daily target per metric.
const PLAN_ROWS = [
  { metric: 'people_lead_engine', label: 'People contacted', sub: 'Lead Engine', kind: 'min', periods: ['month', 'week', 'day'], module: 'lead_engine' },
  { metric: 'reached_lead_engine', label: 'People reached', sub: 'Lead Engine · two-way conversation', kind: 'min', periods: ['month', 'week', 'day'], module: 'lead_engine', reached: true },
  { metric: 'people_t2m', label: 'People contacted', sub: 'T2M (Sales-owned)', kind: 'min', periods: ['month', 'week', 'day'], module: 't2m' },
  { metric: 'reached_t2m', label: 'People reached', sub: 'T2M (Sales-owned) · two-way conversation', kind: 'min', periods: ['month', 'week', 'day'], module: 't2m', reached: true },
  { metric: 'people_pb101', label: 'People contacted', sub: 'PB101 (Sales-owned)', kind: 'min', periods: ['month', 'week', 'day'], module: 'pb101' },
  { metric: 'reached_pb101', label: 'People reached', sub: 'PB101 (Sales-owned) · two-way conversation', kind: 'min', periods: ['month', 'week', 'day'], module: 'pb101', reached: true },
  { metric: 'people_retention_escalated', label: 'People contacted', sub: 'Retention — Escalated', kind: 'min', periods: ['month', 'week', 'day'], module: 'retention_escalated' },
  { metric: 'reached_retention_escalated', label: 'People reached', sub: 'Retention — Escalated · two-way conversation', kind: 'min', periods: ['month', 'week', 'day'], module: 'retention_escalated', reached: true },
  { metric: 'sales_monthly_plan', label: 'Monthly-plan memberships sold', sub: 'Commission tracker', kind: 'min', periods: ['month', 'week'], sales: 'monthly' },
  { metric: 'sales_annual_plan', label: 'Annual-plan memberships sold', sub: 'Commission tracker', kind: 'min', periods: ['month', 'week'], sales: 'annual' },
  { metric: 'overdue_followups', label: 'Overdue follow-ups', sub: 'Keep at or below, any day', kind: 'max', periods: ['month'], snapshot: 'overdue' },
  { metric: 'inbound_waiting', label: 'Inbound leads waiting', sub: 'Keep at or below, any day', kind: 'max', periods: ['month'], snapshot: 'inbound' },
];
const PERIOD_FIELD = { month: 'monthGoal', week: 'weekGoal', day: 'dayGoal' };

function monthKey(offset) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthName(key) {
  return new Date(`${key}-01T12:00:00`).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

function GoalsPanel({ overview, repUserId, repName, isAdmin, onChanged }) {
  const { get, post } = useApi();
  const [offset, setOffset] = useState(0); // 0 = this month, 1 = next (goals get set at month end)
  const [plan, setPlan] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Starts collapsed (BK, 10/7/26: it was opening on every load); remembers the viewer's last choice on this computer.
  const [open, setOpenState] = useState(() => { try { return window.localStorage.getItem('salesGoalsOpen') === '1'; } catch { return false; } });
  const setOpen = fn => setOpenState(prev => {
    const next = typeof fn === 'function' ? fn(prev) : fn;
    try { window.localStorage.setItem('salesGoalsOpen', next ? '1' : '0'); } catch { /* per-viewer convenience only */ }
    return next;
  });
  const month = monthKey(offset);
  const isCurrent = offset === 0;

  const loadPlan = useCallback(() => {
    setError('');
    const rep = repUserId || overview.repUserId;
    get(`/lead/sales-dashboard/goals?repUserId=${encodeURIComponent(rep)}&month=${month}`)
      .then(p => {
        setPlan(p);
        const e = {};
        for (const [metric, it] of Object.entries(p.items || {})) {
          e[metric] = { month: it.monthGoal ?? '', week: it.weekGoal ?? '', day: it.dayGoal ?? '' };
        }
        setEdits(e);
      })
      .catch(err => setError(err.message || 'Could not load goals'));
  }, [repUserId, overview.repUserId, month]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPlan(); }, [loadPlan]);

  if (!plan) return null;
  const locked = plan.status === 'locked';
  const editable = isAdmin && !locked;

  function soFar(row, period) {
    if (!isCurrent) return null;
    if (row.module) return overview.cards?.[row.module]?.periods?.[period]?.contacts?.[row.reached ? 'peopleReached' : 'peopleContacted'] ?? 0;
    if (row.sales) return overview.sales?.[row.sales]?.[period] ?? 0;
    if (row.snapshot === 'overdue') return overview.followUps?.all?.overdue ?? 0;
    if (row.snapshot === 'inbound') return overview.inbound?.total?.new ?? 0;
    return null;
  }
  function setVal(metric, period, v) {
    setEdits(e => ({ ...e, [metric]: { ...(e[metric] || {}), [period]: v } }));
  }
  function statusColor(row, cur, goal) {
    if (goal === '' || goal === null || goal === undefined || cur === null) return '#666';
    const g = Number(goal);
    if (row.kind === 'max') return cur <= g ? '#22c55e' : '#ef4444';
    const pct = g ? cur / g : 1;
    return pct >= 1 ? '#22c55e' : pct >= 0.7 ? '#f59e0b' : '#ef4444';
  }
  async function run(fn) {
    setBusy(true); setError('');
    try { await fn(); loadPlan(); onChanged?.(); }
    catch (e) { setError(e.message || 'Something went wrong'); }
    finally { setBusy(false); }
  }
  const rep = repUserId || overview.repUserId;
  const save = () => post('/lead/sales-dashboard/goals', { repUserId: rep, month, items: edits });
  const doSave = () => run(save);
  const doLock = () => run(async () => { await save(); await post('/lead/sales-dashboard/goals/lock', { repUserId: rep, month }); });
  const doUnlock = () => run(() => post('/lead/sales-dashboard/goals/unlock', { repUserId: rep, month }));

  const inputStyle = { width: 64, background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '4px 6px', fontSize: 12, textAlign: 'right' };
  const cell = { padding: '6px 4px', textAlign: 'right', verticalAlign: 'top' };
  const salesTotal = (field) => {
    const a = edits.sales_monthly_plan?.[field], b = edits.sales_annual_plan?.[field];
    return (a === '' || a === undefined || b === '' || b === undefined) ? '—' : Number(a) + Number(b);
  };

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 700, cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
          🎯 Goals — Inside Sales — {monthName(month)} {open ? '▾' : '▸'}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[[0, 'This month'], [1, 'Next month']].map(([o, label]) => (
            <button key={o} onClick={() => setOffset(o)} style={{
              fontSize: 10, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              border: `1px solid ${offset === o ? '#3b82f6' : '#333'}`, background: offset === o ? '#3b82f615' : '#111',
              color: offset === o ? '#3b82f6' : '#777',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {open && (<>
        <div style={{
          fontSize: 12, padding: '7px 10px', borderRadius: 6, marginBottom: 8,
          background: locked ? '#22c55e18' : '#f59e0b18', color: locked ? '#22c55e' : '#f59e0b',
        }}>
          {locked
            ? `🔒 Locked${plan.lockedBy ? ` by ${plan.lockedBy}` : ''} — agreed together. Only an admin can unlock it.`
            : plan.exists
              ? '✏️ Draft — not agreed yet.'
              : '✏️ No goals set for this month yet.'}
          {!isAdmin && !locked && ' Your admin will enter these with you.'}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ color: '#666', fontSize: 10.5 }}>
              <th style={{ textAlign: 'left', fontWeight: 400 }}>Metric</th>
              <th style={{ textAlign: 'right', fontWeight: 400 }}>Month</th>
              <th style={{ textAlign: 'right', fontWeight: 400 }}>Each week</th>
              <th style={{ textAlign: 'right', fontWeight: 400 }}>Each day</th>
            </tr>
          </thead>
          <tbody>
            {PLAN_ROWS.map(row => (
              <tr key={row.metric} style={{ borderTop: '1px solid #222' }}>
                <td style={{ padding: '6px 4px 6px 0' }}>
                  <div style={{ color: '#ddd' }}>{row.label}</div>
                  <div style={{ fontSize: 10, color: '#777' }}>{row.sub}</div>
                </td>
                {['month', 'week', 'day'].map(period => {
                  if (!row.periods.includes(period)) return <td key={period} style={cell} />;
                  const val = edits[row.metric]?.[period] ?? '';
                  const cur = soFar(row, row.snapshot ? 'month' : period);
                  return (
                    <td key={period} style={cell}>
                      {editable ? (
                        <input type="number" min="0" value={val} style={inputStyle}
                          placeholder={row.kind === 'max' ? 'max' : 'goal'}
                          onChange={e => setVal(row.metric, period, e.target.value)} />
                      ) : (
                        <span style={{ color: val === '' ? '#555' : '#ddd', fontWeight: 600 }}>{val === '' ? '—' : val}</span>
                      )}
                      {cur !== null && (
                        <div style={{ fontSize: 10, color: statusColor(row, cur, val) }}>
                          {row.snapshot ? `${cur} right now` : `${cur} so far`}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr style={{ borderTop: '1px solid #222', color: '#999' }}>
              <td style={{ padding: '6px 4px 6px 0', fontSize: 11 }}>Total memberships sold</td>
              <td style={cell}>
                <b>{salesTotal('month')}</b>
                {isCurrent && <div style={{ fontSize: 10, color: '#777' }}>{(overview.sales?.monthly?.month ?? 0) + (overview.sales?.annual?.month ?? 0)} so far</div>}
              </td>
              <td style={cell}><b>{salesTotal('week')}</b></td>
              <td style={cell} />
            </tr>
          </tbody>
        </table>

        {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{error}</div>}

        {isAdmin && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            {!locked && (<>
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={doSave}>Save draft</button>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={doLock}>🔒 Lock goals</button>
            </>)}
            {locked && (
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={doUnlock}>🔓 Unlock to edit</button>
            )}
          </div>
        )}

        {plan.history?.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 10.5, color: '#666', lineHeight: 1.6 }}>
            <div style={{ color: '#888' }}>History</div>
            {plan.history.slice(0, 5).map((h, i) => (
              <div key={i}>{new Date(h.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} — {h.action}{h.by ? ` by ${h.by}` : ''}</div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 10, color: '#555', marginTop: 8 }}>
          Sales come from the commission tracker (memberships only, counts what's logged there). Overdue follow-ups and inbound waiting are ceilings, not targets.
        </div>
      </>)}
    </div>
  );
}

export default function SalesDashboardModule() {
  const { get } = useApi();
  const { isAdmin } = useAuth();
  const { availableClubs } = useClub();
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [repUserId, setRepUserId] = useState(null);
  // Admins pick a rep from a list that loads first; waiting for it avoids loading the whole dashboard twice
  // (once for the admin, then again for the chosen rep), which doubled the wait on the one-core database.
  const [repsReady, setRepsReady] = useState(!isAdmin);
  // Defaults to every available club explicitly selected (BK, 10/1/26:
  // Josh's real scope is all 6, not a TX-only subset) — never starts
  // narrowed, matching the earlier Win Back TX-only mistake this same
  // session already had to walk back once.
  const [selectedClubs, setSelectedClubs] = useState(() => availableClubs.map(c => c.id));
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (isAdmin) {
      get('/users?role=sales').then(res => {
        const list = res?.users || [];
        setReps(list);
        if (list.length > 0) setRepUserId(list[0].id);
      }).catch(() => {}).finally(() => setRepsReady(true));
    }
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    const params = new URLSearchParams();
    if (repUserId) params.set('repUserId', repUserId);
    if (selectedClubs.length && selectedClubs.length < availableClubs.length) {
      params.set('clubs', selectedClubs.join(','));
    }
    const qs = params.toString();
    get(`/lead/sales-dashboard${qs ? `?${qs}` : ''}`)
      .then(setOverview)
      .catch(e => setLoadError(e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [repUserId, selectedClubs]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (repsReady) load(); }, [load, repsReady]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !overview) return <div className="empty-state"><div className="spinner" /></div>;
  if (loadError) return <div className="empty-state"><p>Couldn't load the sales dashboard: {loadError}</p></div>;
  if (!overview) return <div className="empty-state"><p>No data.</p></div>;

  const clubNameById = Object.fromEntries(availableClubs.map(c => [c.id, c.name]));
  const effectiveClubs = overview.clubs || selectedClubs;

  return (
    <div className="module-page">
      <div className="module-header" style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: 22 }}>📊</span>
          <h1 className="module-title">Sales Dashboard</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ClubFilter availableClubs={availableClubs} selected={selectedClubs} onChange={setSelectedClubs} />
          {isAdmin && reps.length > 1 && (
            <select
              value={repUserId || ''} onChange={e => setRepUserId(e.target.value)}
              style={{ background: '#111', border: '1px solid #333', color: '#ccc', borderRadius: 5, padding: '5px 8px', fontSize: 12 }}
            >
              {reps.map(r => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
            </select>
          )}
        </div>
      </div>

      <LaunchStrip onNavigate={navigate} />

      <GoalsPanel
        overview={overview}
        repUserId={overview.repUserId}
        repName={(reps.find(r => r.id === overview.repUserId) || {}).name || (isAdmin ? '' : 'You')}
        isAdmin={isAdmin}
        onChanged={load}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginTop: 12 }}>
        <InboundCard inbound={overview.inbound} clubNameById={clubNameById} onNavigate={navigate} />
        <FollowUpsCard followUps={overview.followUps} clubs={effectiveClubs} clubNameById={clubNameById} onNavigate={navigate} />
        {ENGINES.map(engine => (
          <EngineCard
            key={engine.module}
            engine={engine}
            card={overview.cards?.[engine.module]}
            onNavigate={navigate}
            clubNameById={clubNameById}
            clubs={effectiveClubs}
          />
        ))}
      </div>
    </div>
  );
}
